import type { Identifiable, Identifier } from '@studio/models';
import { toIdentifier } from '@studio/models';
import type { T_Any, T_Maybe } from '@tryiris-ai/js-types';
import type { Comparable } from './types.js';

/**
 * Compare two model objects by `rank:name:id`.
 *
 * @param a - First object.
 * @param b - Second object.
 * @returns Comparison result.
 */
export const comparatorFn = <T extends Comparable<T_Any>>(a: T, b: T): number => {
  const aKey = `${a.rank}:${a.name}:${a.id}`;
  const bKey = `${b.rank}:${b.name}:${b.id}`;

  return aKey.localeCompare(bKey, undefined, { numeric: true });
};

/**
 * Compare two collections of _ordered_ identifiable-like objects.
 *
 * @param a - First collection.
 * @param b - Second collection.
 * @returns `true` if the collections are equal, `false` otherwise.
 */
export const compareIds = <T extends Identifier = Identifier>(
  a: (Identifiable<T> | T)[],
  b: (Identifiable<T> | T)[]
): boolean => a.map(toIdentifier).join(',') === b.map(toIdentifier).join(',');

/**
 * Given a collection of items, return a unique name of the form `{prefix} {index}`, for example
 * "Group 7" or "Device 2".
 *
 * @param items - Current named items.
 * @param options - Naming options.
 * @param options.prefix - Name prefix.
 * @param options.startingIndex - Starting index (default `1`).
 * @returns Unique name.
 */
export const getUniqueName = (
  items: { name: string }[],
  options: {
    prefix: string;
    startingIndex?: T_Maybe<number>;
  }
): string => {
  const { prefix, startingIndex = 1 } = options;

  // Find a unique name
  let index = startingIndex;

  let uniqueName: T_Maybe<string>;

  while (!uniqueName) {
    const candidate = `${prefix} ${index++}`;

    if (!items.some((µ) => µ.name === candidate)) {
      uniqueName = candidate;
    }
  }

  return uniqueName;
};
